import React from "react";
import { Box, Button, Container, Paper, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useProjectLeads from "../../hooks/projectLeads/useProjectLeads";
import { CreateProjectLeadDto } from "../../types/projectLeadTypes";

/**
 * Page to create a new Lead — the rough precursor to a Projektausschreibung.
 * Every neoMDB user can create a Lead. Only the HoCR can then convert it to
 * project data or discard it.
 */
const CreateProjectLead = () => {
  const navigate = useNavigate();
  const { createProjectLead } = useProjectLeads();

  const [formData, setFormData] = React.useState<CreateProjectLeadDto>({
    projectName: "",
    situation: "",
    timeframe: "",
    revenue: "",
    customer: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [nameError, setNameError] = React.useState(false);

  const handleChange =
    (field: keyof CreateProjectLeadDto) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (field === "projectName" && e.target.value.trim().length > 0) {
        setNameError(false);
      }
    };

  const handleSubmit = async () => {
    if (!formData.projectName.trim()) {
      setNameError(true);
      return;
    }
    try {
      setSubmitting(true);
      const created = await createProjectLead({
        projectName: formData.projectName.trim(),
        situation: formData.situation?.trim() || null,
        timeframe: formData.timeframe?.trim() || null,
        revenue: formData.revenue?.trim() || null,
        customer: formData.customer?.trim() || null,
      });
      navigate(`/leads/${created.leadId}`);
    } catch {
      // The mutation hook already shows the failure toast; keep the page from throwing into the runtime overlay.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
        Neuen Lead anlegen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ein Lead ist eine noch unreife Projekt-Anfrage. Zu diesem Zeitpunkt sind viele Informationen noch offen — fülle
        aus, was du weißt. Der HoCR entscheidet später, ob daraus eine Ausschreibung wird.
      </Typography>
      <Paper sx={{ paddingX: 4, paddingY: 3 }}>
        <Stack spacing={3}>
          <TextField
            label="Projektname"
            required
            fullWidth
            value={formData.projectName}
            onChange={handleChange("projectName")}
            error={nameError}
            helperText={nameError ? "Projektname ist erforderlich" : undefined}
          />
          <TextField
            label="Ausgangssituation / Zielsetzung"
            fullWidth
            multiline
            minRows={3}
            value={formData.situation ?? ""}
            onChange={handleChange("situation")}
            helperText="Grobe Beschreibung der Anfrage — was möchte der Kunde erreichen?"
          />
          <TextField
            label="Zeitraum"
            fullWidth
            value={formData.timeframe ?? ""}
            onChange={handleChange("timeframe")}
            placeholder='z.B. "Sommer 2026, voraussichtlich April bis Q3"'
            helperText="Freitext — grober zeitlicher Rahmen"
          />
          <TextField
            label="Umsatz"
            fullWidth
            value={formData.revenue ?? ""}
            onChange={handleChange("revenue")}
            placeholder='z.B. "ca. 30-40 BT, ca. 900€/BT"'
            helperText="Freitext — grobe Einordnung des Projektvolumens"
          />
          <TextField
            label="Kunde"
            fullWidth
            value={formData.customer ?? ""}
            onChange={handleChange("customer")}
            placeholder="z.B. Lebensmittel AG"
            helperText="Freitext — noch kein Unternehmensdatensatz nötig"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} disabled={submitting}>
              Abbrechen
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              Lead anlegen
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default CreateProjectLead;
